import i18n from '@/i18n';

export interface PricingProduct {
  slug: string;
  name: string;
  description: string;
  features: string[];
  monthlyPrice: number;
  yearlyPrice: number;
  accent: string;
  icon: string;
  popular?: boolean;
}

/**
 * Canonical pricing: all products are bundled in Pro at €50/mo.
 * Per-product prices kept for reference but the store surfaces
 * a single €50/mo Pro plan via PricingPage and StorePage.
 */
export const pricingProducts: PricingProduct[] = [
  {
    slug: 'kol',
    name: 'KOL Network',
    description:
      'Map and score key opinion leaders across therapeutic areas with 5-axis scoring methodology.',
    features: [
      'KOL Identification & Scoring',
      'Rising Stars Detection',
      'Network Analysis',
      'Digital Footprint Radar',
      'Unlimited KOL profiles',
    ],
    monthlyPrice: 50,
    yearlyPrice: 50,
    // DESIGN §1/§11: in-palette product accents only (no green/violet/indigo/amber).
    accent: 'var(--signal-blue)', // Signal blue (was #3B82F6 blue-500 — off-palette)
    icon: 'users',
    popular: true,
  },
  {
    slug: 'medwatch',
    name: 'Medical Watch',
    description:
      'Real-time monitoring of publications, clinical trials, and regulatory signals.',
    features: [
      'PubMed Integration',
      'Clinical Trial Tracking',
      'FDA/EMA Alerts',
      'Signal Detection',
      'Unlimited therapeutic areas',
    ],
    monthlyPrice: 50,
    yearlyPrice: 50,
    accent: 'var(--grey-2)', // §6 warm grey 2 (was #10B981 emerald — off-palette)
    icon: 'activity',
  },
  {
    slug: 'persona',
    name: 'Persona Sandbox',
    description:
      'Generate detailed HCP and patient personas powered by AI and real-world ontology data.',
    features: [
      'AI-Generated Personas',
      'Ontology-Powered Profiles',
      'Channel Recommendations',
      'Export & Share',
      'Unlimited personas',
    ],
    monthlyPrice: 50,
    yearlyPrice: 50,
    accent: 'var(--ink)', // ink (was #8B5CF6 violet — off-palette)
    icon: 'user-circle',
  },
  {
    slug: 'market-access',
    name: 'Market Access',
    description:
      'Track regulatory decisions across HAS, NICE, G-BA, and EMA. Compare outcomes by country.',
    features: [
      'Multi-Country Tracking',
      'ASMR/SMR Ratings',
      'Decision Timeline',
      'Country Comparison',
      'Unlimited countries',
    ],
    monthlyPrice: 50,
    yearlyPrice: 50,
    accent: 'var(--grey-3)', // §6 warm grey 3 (was #F59E0B amber — off-palette)
    icon: 'shield',
  },
];

/**
 * A money value with its unit made EXPLICIT — either minor units (cents, the
 * Stripe convention) or major units (whole currency). The unit is part of the
 * type so no caller can silently pass the wrong magnitude: this is what closes
 * the historical 100× risk where one caller divided by 100 and another did not.
 */
export type CurrencyAmount = { minor: number } | { major: number };

/**
 * The single money formatter for the store-front. Always 2 fraction digits and
 * the i18n runtime locale (falls back to the current `i18n.language`). Callers
 * declare the unit explicitly via {@link CurrencyAmount}; there is no implicit
 * ÷100 anywhere else in the app.
 *
 * @example formatCurrency({ minor: 4900 }, 'eur')  // "€49.00" (en) / "49,00 €" (fr)
 * @example formatCurrency({ major: 49 }, 'EUR')    // "€49.00"
 */
export function formatCurrency(
  amount: CurrencyAmount,
  currency = 'EUR',
  options: { locale?: string; fractionDigits?: number } = {},
): string {
  const major = 'minor' in amount ? amount.minor / 100 : amount.major;
  const fractionDigits = options.fractionDigits ?? 2;
  const locale = options.locale ?? i18n.language;
  const code = (currency || 'EUR').toUpperCase();
  try {
    return new Intl.NumberFormat(locale || undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(major);
  } catch {
    return `${major.toFixed(fractionDigits)} ${code}`;
  }
}

/**
 * The single date formatter for the billing surfaces. Same contract as
 * {@link formatCurrency} directly above: the caller passes the runtime locale,
 * and an omitted one falls back to `i18n.language` rather than to a country.
 *
 * It used to hard-code 'en-GB', so the French billing screen printed
 * "Prochaine facturation : 1 Dec 2026" — the sentence translated, the month
 * not. A month name is interface text that no catalogue holds and no key
 * audit can see; only the formatter's locale decides it. `os/pages/ChatsPage`
 * already took a locale, which is why the OS never had this bug.
 *
 * @example formatDate(iso, 'fr') // "1 déc. 2026"
 * @example formatDate(iso, 'es') // "1 dic 2026"
 */
export function formatDate(dateString: string, locale?: string): string {
  const tag = locale || i18n.language;
  try {
    return new Date(dateString).toLocaleDateString(tag || undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    // An unsupported tag throws RangeError; the host default still beats a crash.
    return new Date(dateString).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }
}
